import { Draft, PayloadAction } from '@reduxjs/toolkit';
import { SearchError } from 'gql/autogen/SearchError';
import { logError, logInfo, logWarn } from 'core-framework/util/LogMessage';
import { getDocument, getLocationHref } from 'core-framework/util/Dom';
import { ActivitiesGqlResponseType, ActivityGqlOperation, ApiBaseState, GqlApiErrorWithResponseInfo, GqlApiResponse, GqlResponseError, SerializedError } from 'store/api/api.types';
import { Optional } from 'core/type/generic';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { getGeoLogTags } from 'core/service/measurements/measurementUtils';
import axios, { AxiosError, AxiosRequestHeaders, AxiosResponse, AxiosResponseHeaders, Method } from 'axios';
import { SearchErrorCode } from 'gql/autogen/SearchErrorCode';
import { SearchSubErrorCode } from 'gql/autogen/SearchSubErrorCode';
import { FieldValue } from 'core/constant/FieldValue';

export enum GqlHeaders {
    CorrelationId = 'ag-correlation-id',
    RetryAttempt = 'AG-RETRY-ATTEMPT',
    RequestId = 'AG-REQUEST-ID',
    RequestAttempt = 'AG-REQUEST-ATTEMPT',
}

const EmptyRequestHeaders = new Headers();

export const convertError = (error?: SerializedError): Optional<{ message: string; status: string }> => {
    if (!error) return undefined;

    return {
        status: error.status ? String(error.status) : error.code || '0',
        message: error.message || error.error || 'no-message',
    };
};

export const combineGqlErrors = (errors?: SearchError[]): GqlResponseError => {
    // There will always be maximum 1 error - confirmed by backend
    if (errors && errors.length > 0) {
        const error = errors[0];
        const internalErrors = errors.filter((e) => {
            const errorCode = e?.errorCode?.toLowerCase();
            return errorCode === 'internalerror' || errorCode === SearchErrorCode.INTERNAL_ERROR.toLowerCase() || errorCode === SearchErrorCode.TECHNICAL_ERROR.toLowerCase();
        });
        const isInternalError = internalErrors.length > 0;
        return {
            shouldRetry: false,
            status: isInternalError ? 500 : 400,
            message:
                errors
                    .map((e) => e && e.message)
                    .filter((m) => m)
                    .join(', ') || 'empty message',
            errorCode: error.errorCode as SearchErrorCode,
            subErrorCode: error.subErrorCode as SearchSubErrorCode,
        };
    }
    return { shouldRetry: false, message: 'undefined', status: 500, errorCode: SearchErrorCode.EMPTY_ERROR_CODE, subErrorCode: SearchSubErrorCode.EMPTY_SUB_ERROR_CODE };
};

export const getResponseError = (queryResponse: AxiosResponse<ActivitiesGqlResponseType> | undefined, operation = ActivityGqlOperation.search): GqlResponseError => {
    const data = queryResponse?.data?.data;
    const response = data && operation in data ? data[operation] : {};
    if (!response.isSuccess) {
        return combineGqlErrors(response.errors);
    }
    if (response.result && !response.result.isCompleted) {
        // HTTP 206: Partial Content
        return {
            shouldRetry: true,
            message: 'Incomplete response',
            status: 206,
            errorCode: SearchErrorCode.INCOMPLETE_RESPONSE,
            subErrorCode: SearchSubErrorCode.PARTIAL_CONTENT,
        };
    }
    return { shouldRetry: false, message: '', status: 200 };
};

export const updateApiBaseState = <T, V>(state: Draft<ApiBaseState>, action: PayloadAction<GqlApiResponse<T, V>>) => {
    const baseState = action.payload.baseState;
    state.isSuccess = baseState.isSuccess;
    state.isError = baseState.isError;
    state.isLoading = baseState.isLoading;
    state.error = baseState.error;
    return state;
};

export const getValueFromAxiosHeaders = (headers: AxiosRequestHeaders | AxiosResponseHeaders | Headers, key: string, defaultValue = ''): string => {
    if (headers && headers.has('AG-RETRY-ATTEMPT')) {
        const headerValue = headers.get('AG-RETRY-ATTEMPT');
        return headerValue === null ? defaultValue : String(headerValue);
    }
    return defaultValue;
};

const getCorrelationIdFromError = (error?: AxiosError<unknown, unknown>): string => {
    if (!error) {
        return '';
    }
    const correlationIdFromRequest = getValueFromAxiosHeaders(error.config?.headers ?? EmptyRequestHeaders, GqlHeaders.CorrelationId);
    if (error.response?.headers) {
        return getValueFromAxiosHeaders(error.response.headers as AxiosResponseHeaders, GqlHeaders.CorrelationId, correlationIdFromRequest);
    }
    return correlationIdFromRequest;
};

export const getGqlApiErrorFromAxiosError = (error: AxiosError<ActivitiesGqlResponseType>, operation: ActivityGqlOperation): GqlApiErrorWithResponseInfo => {
    const correlationId: string = getCorrelationIdFromError(error);
    const status = error.response?.status || 200;
    const responseError = getResponseError(error.response, operation);
    const requestHeader = error.request ? (error.request as { headers: AxiosRequestHeaders })?.headers : EmptyRequestHeaders;

    return {
        correlationId,
        operation,
        status,
        message: error.message,
        data: responseError,
        name: responseError.errorCode || SearchErrorCode.EMPTY_ERROR_CODE,
        errorCode: responseError.errorCode,
        subErrorCode: responseError.subErrorCode,
        shouldRetry: String(responseError.shouldRetry),
        isUnhandledError: true,
        retryAttempt: getValueFromAxiosHeaders(requestHeader, GqlHeaders.RetryAttempt, '0'),
        requestId: getValueFromAxiosHeaders(requestHeader, GqlHeaders.RequestId, '0'),
        retryCount: getValueFromAxiosHeaders(requestHeader, GqlHeaders.RequestAttempt, '0'),
    };
};

export const logGql = (bootstrap: BootstrapConfig, error: GqlApiErrorWithResponseInfo, pageTypeId: number, logLevel = 'error') => {
    const defaultEventData = {
        cookieLength: getDocument()?.cookie?.length,
        url: getLocationHref(),
    };

    const geoTags = getGeoLogTags(bootstrap);
    const customTags = {
        ...geoTags,
        type: 'graphql-error',
        requestType: 'graphql-ajax',
        pageTypeId: String(pageTypeId),
        code: String(error.status),
        errorCode: error.errorCode || SearchErrorCode.EMPTY_ERROR_CODE,
        subErrorCode: error.subErrorCode || SearchSubErrorCode.EMPTY_SUB_ERROR_CODE,
        message: error.message,
        correlationId: error.correlationId,
        retryAttempt: error.retryAttempt,
        requestId: error.requestId,
        retryCount: error.retryCount,
        operation: error.operation,
    };

    if (logLevel === 'info') {
        logInfo(error.message, pageTypeId, customTags, defaultEventData);
    }
    if (logLevel === 'warn') {
        logWarn(error.message, pageTypeId, customTags, defaultEventData);
    } else {
        logError(error.message, pageTypeId, customTags, defaultEventData);
    }
};

export const isQueryLoading = (queryState: { isFetching: boolean; isLoading: boolean; isUninitialized: boolean }): boolean => {
    return queryState.isFetching || queryState.isLoading || queryState.isUninitialized;
};

export const toSafeString = <T>(value: T) => (value != null ? String(value) : FieldValue.Undefined);

export const fetchResponse = (url: string, init: { headers?: Record<string, string>; body?: unknown; method?: Method }) => {
    return axios<Record<string, unknown>>({
        url,
        headers: init.headers,
        data: init.body,
        method: init.method || (init.body ? 'POST' : 'GET'),
    }).then((response) => {
        return { body: response.data };
    });
};
